import { useCallback } from 'react';
import { Col, Row } from 'reactstrap';
import { useSelector } from 'react-redux';
import { RootState } from 'slices';
import styles from 'styles/common/pagination.module.css';
import { useDispatch } from 'react-redux';

interface EntriesPaginationProps {
  customPageSize: number;
  setEntriesPerPage: (value: number) => void;
  currentPage: number;
  totalPages: number;
  handlePageChange: (value: number) => void;
  handlePreviousPage: () => void;
  handleNextPage: () => void;
  inputPageValue: number | null;
  setInputPageValue: (value: number | null) => void;
  itemsPerPage?: number[];
}
interface ResultsPaginationProps {
  showingData: number;
  TotalData: number;
  currentPage: number;
  totalPages: number;
  handlePageChange: (value: number) => void;
  handlePreviousPage: () => void;
  handleNextPage: () => void;
  inputPageValue: number | null;
  setInputPageValue: (value: number | null) => void;
}
interface PaginationProps {
  currentPage: number;
  totalPages: number;
  handlePageChange: (value: number) => void;
  handlePreviousPage: () => void;
  handleNextPage: () => void;
  inputPageValue: number | null;
  setInputPageValue: (value: number | null) => void;
  showInputPageValue?: boolean;
}

export const EntriesPagination = ({
  customPageSize,
  setEntriesPerPage,
  currentPage,
  totalPages,
  handlePageChange,
  handlePreviousPage,
  handleNextPage,
  inputPageValue,
  setInputPageValue,
  // itemsPerPage = [10, 25, 50],
  itemsPerPage = [25],
}: EntriesPaginationProps) => {
  const dispatch = useDispatch();
  return (
    <Row className="mb-3 align-items-center g-3 text-center text-sm-start">
      <Col lg={4} xs={12}>
        <div className="col-sm d-flex flex-wrap align-items-center justify-content-lg-start justify-content-center">
          {/* <div className="col-sm"> */}
          <div className="text-muted">
            Show{' '}
            <select
              name="entriesPerPage"
              data-testid="SelectEntriesPerPage"
              value={customPageSize}
              onChange={(e) => {
                dispatch(setEntriesPerPage(Number(e.target.value)));
                handlePageChange(0);
                dispatch(setInputPageValue(1));
              }}
              className="form-select d-inline-block w-auto fw-semibold text-muted"
              style={{ height: 38 }}
            >
              {itemsPerPage.map((size) => (
                <option
                  key={size}
                  value={size}
                  className="fw-semibold text-muted"
                >
                  {size}
                </option>
              ))}
            </select>{' '}
            entries per page
          </div>
        </div>
      </Col>

      <Col lg={8} xs={12} className="px-0">
        <Pagination
          key={1}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
          inputPageValue={inputPageValue}
          setInputPageValue={setInputPageValue}
        />
      </Col>
    </Row>
  );
};

export const ResultsPagination = ({
  showingData,
  TotalData,
  currentPage,
  totalPages,
  handlePageChange,
  handlePreviousPage,
  handleNextPage,
  inputPageValue,
  setInputPageValue,
}: ResultsPaginationProps) => {
  return (
    <Row className="mb-3 align-items-center g-3 text-center text-sm-start">
      <Col lg={4} xs={12}>
        <div className="col-sm d-flex flex-wrap align-items-center justify-content-lg-start justify-content-center">
          {/* <div className="col-sm"> */}
          <div className="text-muted">
            Showing
            <span className="fw-semibold ms-1" data-testid="showingPromos">
              {showingData}
            </span>{' '}
            of{' '}
            <span className="fw-semibold" data-testid="totalPromos">
              {TotalData}
            </span>{' '}
            Results
          </div>
        </div>
      </Col>

      <Col lg={8} xs={12} className="px-0">
        <Pagination
          key={2}
          currentPage={currentPage}
          totalPages={totalPages}
          handlePageChange={handlePageChange}
          handlePreviousPage={handlePreviousPage}
          handleNextPage={handleNextPage}
          inputPageValue={inputPageValue}
          setInputPageValue={setInputPageValue}
        />
      </Col>
    </Row>
  );
};

export const Pagination = ({
  currentPage,
  totalPages,
  handlePageChange,
  handlePreviousPage,
  handleNextPage,
  inputPageValue,
  setInputPageValue,
  showInputPageValue = true,
}: PaginationProps) => {
  const layoutState = useSelector((state: RootState) => state.Layout);
  const { layoutModeType } = layoutState;
  const isDarkMode = layoutModeType === 'dark';
  const dispatch = useDispatch();

  const handleFirstPage = () => {
    handlePageChange(0);
    dispatch(setInputPageValue(1));
  };
  const handleLastPage = () => {
    handlePageChange(totalPages - 1);
    dispatch(setInputPageValue(totalPages));
  };
  const getResponsiveRange = useCallback(() => {
    const screenWidth = window.innerWidth;
    if (screenWidth < 576) return 1;
    if (screenWidth < 768) return 2;
    return 3;
  }, []);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const range = getResponsiveRange();
    const start = Math.max(0, currentPage - range);
    const end = Math.min(totalPages, currentPage + range + 1);

    for (let i = start; i < end; i++) {
      pageNumbers.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? 'active' : ''}`}
          style={{ zIndex: 0 }}
        >
          <button
            className={`page-link ${isDarkMode ? styles['dark-mode-btn'] : ''}`}
            onClick={() => {
              dispatch(setInputPageValue(i + 1));
              handlePageChange(i);
            }}
          >
            {i + 1}
          </button>
        </li>
      );
    }
    return pageNumbers;
  };

  return (
    <div className="d-flex flex-wrap align-items-center justify-content-lg-end justify-content-center px-lg-2 px-0">
      <div className="col-xxl-6 mb-2 mb-md-0 d-flex justify-content-lg-end justify-content-center">
        <ul className="pagination pagination-separated pagination-md mb-0">
          <li className={`page-item ${currentPage === 0 ? 'disabled' : ''}`}>
            <button
              className={`page-link ${
                isDarkMode ? styles['dark-mode-btn'] : ''
              }`}
              onClick={handleFirstPage}
              disabled={currentPage === 0}
            >
              &laquo;
            </button>
          </li>
          <li className={`page-item ${currentPage === 0 ? 'disabled' : ''}`}>
            <button
              className={`page-link ${
                isDarkMode ? styles['dark-mode-btn'] : ''
              }`}
              onClick={handlePreviousPage}
              disabled={currentPage === 0}
            >
              &lsaquo;
            </button>
          </li>
          {renderPageNumbers()}
          <li
            className={`page-item ${
              currentPage === totalPages - 1 ? 'disabled' : ''
            }`}
          >
            <button
              className={`page-link ${
                isDarkMode ? styles['dark-mode-btn'] : ''
              }`}
              onClick={handleNextPage}
              disabled={currentPage === totalPages - 1}
            >
              &rsaquo;
            </button>
          </li>
          <li
            className={`page-item ${
              currentPage === totalPages - 1 ? 'disabled' : ''
            }`}
          >
            <button
              className={`page-link ${
                isDarkMode ? styles['dark-mode-btn'] : ''
              }`}
              onClick={handleLastPage}
              disabled={currentPage === totalPages - 1}
            >
              &raquo;
            </button>
          </li>
        </ul>
      </div>
      {showInputPageValue && (
        <div className="col-xxl-2 col-12 d-flex align-items-center justify-content-center">
          <div className="input-group text-muted w-100 d-flex justify-content-lg-end m-1 justify-content-center">
            {/* <div className="input-group text-muted w-100 d-flex justify-content-center"> */}
            <input
              type="text"
              className="form-control text-center text-muted"
              style={{ height: 38, maxWidth: 70 }}
              value={inputPageValue === null ? '' : inputPageValue}
              onChange={(e) => {
                const value = e.target.value;
                if (value !== '' && !isNaN(Number(value))) {
                  dispatch(setInputPageValue(Number(value)));
                  handlePageChange(
                    Math.min(
                      totalPages - 1,
                      Math.max(0, Number(e.target.value) - 1)
                    )
                  );
                } else {
                  dispatch(setInputPageValue(null));
                }
              }}
            />

            <span
              style={{ height: 38, maxWidth: 70 }}
              className="input-group-text text-muted"
            >
              of {totalPages}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
